import { Box, Collapse, Switch, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { SPACING } from "../../constants/spacing";
import { useSettings } from "../../contexts/SettingsContext";

const ViewSettings = () => {
  const { t } = useTranslation("settings");
  const {
    showSalesOverTime,
    setShowSalesOverTime,
    showRentalsOverTime,
    setShowRentalsOverTime,
    showImpressionsOverTime,
    setShowImpressionsOverTime,
    showImpressionRevenueOverTime,
    setShowImpressionRevenueOverTime,
    showTotalImpressionsCard,
    setShowTotalImpressionsCard,
    showTotalSalesCard,
    setShowTotalSalesCard,
    showTotalRevenueCard,
    setShowTotalRevenueCard,
    showSalesDetails,
    setShowSalesDetails,
    showDetailedBreakdown,
    setShowDetailedBreakdown,
    showAnimatedNumbers,
    setShowAnimatedNumbers,
    animatedNumberSections,
    setAnimatedNumberSections,
  } = useSettings();

  const toggleSection = (key) => {
    setAnimatedNumberSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const viewSettings = [
    {
      label: t("viewSettings.settings.salesOverTime.label"),
      description: t("viewSettings.settings.salesOverTime.description"),
      checked: showSalesOverTime,
      onChange: () => setShowSalesOverTime(!showSalesOverTime),
    },
    {
      label: t("viewSettings.settings.rentalsOverTime.label"),
      description: t("viewSettings.settings.rentalsOverTime.description"),
      checked: showRentalsOverTime,
      onChange: () => setShowRentalsOverTime(!showRentalsOverTime),
    },
    {
      label: t("viewSettings.settings.impressionsOverTime.label"),
      description: t("viewSettings.settings.impressionsOverTime.description"),
      checked: showImpressionsOverTime,
      onChange: () => setShowImpressionsOverTime(!showImpressionsOverTime),
    },
    {
      label: t("viewSettings.settings.impressionRevenueOverTime.label"),
      description: t(
        "viewSettings.settings.impressionRevenueOverTime.description",
      ),
      checked: showImpressionRevenueOverTime,
      onChange: () =>
        setShowImpressionRevenueOverTime(!showImpressionRevenueOverTime),
    },
    {
      label: t("viewSettings.settings.totalImpressionsCard.label"),
      description: t("viewSettings.settings.totalImpressionsCard.description"),
      checked: showTotalImpressionsCard,
      onChange: () => setShowTotalImpressionsCard(!showTotalImpressionsCard),
    },
    {
      label: t("viewSettings.settings.totalSalesCard.label"),
      description: t("viewSettings.settings.totalSalesCard.description"),
      checked: showTotalSalesCard,
      onChange: () => setShowTotalSalesCard(!showTotalSalesCard),
    },
    {
      label: t("viewSettings.settings.totalRevenueCard.label"),
      description: t("viewSettings.settings.totalRevenueCard.description"),
      checked: showTotalRevenueCard,
      onChange: () => setShowTotalRevenueCard(!showTotalRevenueCard),
    },
    {
      label: t("viewSettings.settings.salesDetails.label"),
      description: t("viewSettings.settings.salesDetails.description"),
      checked: showSalesDetails,
      onChange: () => setShowSalesDetails(!showSalesDetails),
    },
    {
      label: t("viewSettings.settings.detailedBreakdown.label"),
      description: t("viewSettings.settings.detailedBreakdown.description"),
      checked: showDetailedBreakdown,
      onChange: () => setShowDetailedBreakdown(!showDetailedBreakdown),
    },
  ];

  const sectionSettings = [
    {
      key: "dashboardCards",
      label: t("viewSettings.settings.animatedSections.dashboardCards"),
    },
    {
      key: "chartTotals",
      label: t("viewSettings.settings.animatedSections.chartTotals"),
    },
    {
      key: "statsCards",
      label: t("viewSettings.settings.animatedSections.statsCards"),
    },
    {
      key: "tables",
      label: t("viewSettings.settings.animatedSections.tables"),
    },
    {
      key: "sourceDetails",
      label: t("viewSettings.settings.animatedSections.sourceDetails"),
    },
    {
      key: "admin",
      label: t("viewSettings.settings.animatedSections.admin"),
    },
  ];

  const SettingRow = ({ label, description, checked, onChange, indent }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        py: indent ? 1.5 : 3,
        pl: indent ? 4 : 0,
      }}
    >
      <Box>
        <Typography
          variant="bodyMd"
          sx={{ fontWeight: 500, mb: 0.5, display: "block" }}
        >
          {label}
        </Typography>
        {description && (
          <Typography
            variant="bodyMd"
            sx={{ color: "text.secondary", display: "block" }}
          >
            {description}
          </Typography>
        )}
      </Box>
      <Box sx={{ minWidth: 60, display: "flex", justifyContent: "flex-end" }}>
        <Switch checked={checked} onChange={onChange} color="primary" />
      </Box>
    </Box>
  );

  return (
    <Box>
      <Typography variant="h2" sx={{ mb: SPACING.sm }}>
        {t("viewSettings.title")}
      </Typography>

      <Typography
        variant="bodyMd"
        component="p"
        sx={{ color: "text.secondary", pb: SPACING.lg }}
      >
        {t("viewSettings.description")}
      </Typography>

      {viewSettings.map((setting, index) => (
        <SettingRow key={index} {...setting} />
      ))}

      {/* Animated Numbers — master toggle */}
      <SettingRow
        label={t("viewSettings.settings.animatedNumbers.label")}
        description={t("viewSettings.settings.animatedNumbers.description")}
        checked={showAnimatedNumbers}
        onChange={() => setShowAnimatedNumbers(!showAnimatedNumbers)}
      />

      {/* Per-section toggles — collapse when master is off */}
      <Collapse in={showAnimatedNumbers}>
        <Box
          sx={{
            borderLeft: 2,
            borderColor: "divider",
            ml: 1,
            mb: 2,
          }}
        >
          {sectionSettings.map((section) => (
            <SettingRow
              key={section.key}
              label={section.label}
              checked={animatedNumberSections[section.key] !== false}
              onChange={() => toggleSection(section.key)}
              indent
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

export default ViewSettings;
